import {
  selectDisplayColor,
  selectDisplayLabel,
  type SelectColor,
} from '@/lib/databases/values';
import { createElement } from 'react';

import type { BlockRecord } from '@/lib/blocks/types';
import type { BlockPropertyRecord, DatabaseColumnDefinition } from '@/lib/databases/types';

import { SelectChip } from './SelectChip.web';

type DatabaseListProps = {
  rows: BlockRecord[];
  columns: DatabaseColumnDefinition[];
  propertiesByRowId: Record<string, BlockPropertyRecord[]>;
  onOpenRow?: (rowId: string) => void;
  onPeekRow?: (rowId: string) => void;
};

type ListChip = { key: string; label: string; color: SelectColor | null };

function stopBubble(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

function selectChips(
  columns: DatabaseColumnDefinition[],
  properties: BlockPropertyRecord[],
): ListChip[] {
  const chips: ListChip[] = [];
  for (const col of columns) {
    if (col.type !== 'select' && col.type !== 'multi_select') {
      continue;
    }
    const prop = properties.find((p) => p.propertyId === col.id);
    const value = prop?.value;
    if (!value || typeof value !== 'object' || !('t' in value)) {
      continue;
    }
    const raw = (value as { v?: unknown }).v;
    const ids =
      col.type === 'multi_select'
        ? Array.isArray(raw)
          ? raw.map(String)
          : []
        : raw != null && String(raw) !== ''
          ? [String(raw)]
          : [];
    for (const id of ids) {
      const label = selectDisplayLabel(col.config, id);
      if (!label) {
        continue;
      }
      chips.push({ key: `${col.id}:${id}`, label, color: selectDisplayColor(col.config, id) });
    }
  }
  return chips;
}

function pageIcon() {
  return createElement(
    'svg',
    {
      className: 'cs-db-list-row-icon',
      width: 16,
      height: 16,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.8,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      'aria-hidden': true,
    },
    createElement('path', {
      d: 'M14 3v4a1 1 0 0 0 1 1h4',
    }),
    createElement('path', {
      d: 'M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z',
    }),
  );
}

export function DatabaseList({
  rows,
  columns,
  propertiesByRowId,
  onOpenRow,
  onPeekRow,
}: DatabaseListProps) {
  return createElement(
    'div',
    { className: 'cs-db-list', onMouseDown: stopBubble, onKeyDownCapture: stopBubble },
    rows.map((row) => {
          const props = propertiesByRowId[row.id] ?? [];
          const chips = selectChips(columns, props);
          return createElement(
            'button',
            {
              key: row.id,
              type: 'button',
              className: 'cs-db-list-row',
              onClick: () => (onPeekRow ?? onOpenRow)?.(row.id),
              onDoubleClick: () => onOpenRow?.(row.id),
              onMouseDown: stopBubble,
            },
            pageIcon(),
            createElement(
              'span',
              { className: 'cs-db-list-row-title' },
              row.title?.trim() || 'Untitled',
            ),
            chips.length > 0
              ? createElement(
                  'span',
                  { className: 'cs-db-list-row-props' },
                  chips.map((c) =>
                    createElement(SelectChip, { key: c.key, label: c.label, color: c.color }),
                  ),
                )
              : null,
          );
        }),
  );
}
